<template>
  <section
    class="media-buying-detail media-buying-detail-tb public-table media-buying-ad"
    :class="{
      wavebg: loading,
    }"
  >
    <PostsViewTable
      ref="dataTable"
      :status-channel="statusChannel"
      :status-campaign="statusCampaign"
      :active-tab="activeTab"
      :table-sorting="sortings"
      @update="update"
    />
  </section>
</template>

<script>
import eventBus from '@/lib/eventBus';
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import { pageSizeMixin } from '@/views/Dashboard/Reports/page_size';
import PostsViewTable from '@/views/Dashboard/MediaBuying/PostsView/PostsViewTable.vue';
import { fixRange } from '@/views/Dashboard/Reports/dateMixin';
import { debounce } from '@/lib/lodash';

const fieldMap = {
  post_status: 'status',
  post_type: 'type',
  period: 'post_period_start',
  post_sum: 'amount',
  payment_status: 'status',
  payment_sum: 'amount_usd',
};

const defaultSortings = {
  page: 1,
  pageSize: 20,
  search: '',
  prop: 'created_at',
  order: 'desc',
  status: JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'))?.statusPosts || '',
  type: JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'))?.typePosts || '',

  created_at_from: JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'))?.created_at_from
    || '',
  created_at_to: JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'))?.created_at_to
    || '',
  period: JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'))?.period || 2,

  needs_attention_first: true,
};

const fetch = async (ctx, params) => {
  ctx.cacheParams = ctx.$_.cloneDeep(params);

  let fixed = null;
  const period = {
    period: params.period,
    from: params.created_at_from,
    to: params.created_at_to,
  };

  fixed = fixRange(period);

  if (fixed) {
    period.from = fixed.from;
    period.to = fixed.to;
  }

  const paymentsParams = {
    search: params.search || '',
    limit: params.limit || defaultSortings.pageSize,
    offset: (params.offset * params.limit) || 0,

    sort_column: fieldMap[params.prop]
      || params.prop || defaultSortings.prop,

    sort_dir: params.order || defaultSortings.order,
    needs_attention_first: params.needs_attention_first !== undefined 
      ? params.needs_attention_first 
      : defaultSortings.needs_attention_first,
    status: params.status !== undefined ? params.status : defaultSortings.status,
    type: params.type !== undefined ? params.type : defaultSortings.type,

    created_at_from: period.from !== undefined ? period.from : defaultSortings.created_at_from,
    created_at_to: period.to !== undefined ? period.to : defaultSortings.created_at_to,
    period: params.period !== undefined ? params.period : defaultSortings.period,

    campaign_id: null,
  };

  const posts = await ctx.$store.dispatch('getPosts', paymentsParams);
  return [posts];
};

export default {
  name: 'PostsView',

  fetch,
  components: {
    PostsViewTable,
  },
  mixins: [UrlMixin, UrlMixinHooks, InjectPlugins, pageSizeMixin],
  props: ['passedParams'],

  data() {
    return {
      cacheParams: {},
      subRootId: null,
      balances: {
        approved: {},
        paid: {},
        spent: {},
        pending: {},
        total: {},
      },
      isLoadingCampaign: true,
      payment_requests: 'parent',
      cost_traffic_tracking: 'parent',
      loading: false,
      activeTab: '',
      campaign_name: '',
      campaign_description: '',
      channel_id: '',
      campaign_id: '',
      balance_sum: '',
      spent_sum: '',
      paid_sum: '',
      payments_on_review_sum: '',
      cost_model: '',
      sortings: defaultSortings,
      statusChannel: 'active',
      statusCampaign: 'active',
      interval: null,
      listener: '',
      querySum: {
        query: {},
        sub_affiliate_id: null,
        passedPeriod: {
          period: 2,
          from: '',
          to: '',
        },
        period: {},
        filters: [],
      },
    };
  },

  watch: {
    urlQuery(v) {
      if (this.$route.name === 'routes.mediaBuyingPosts' && v.activeTab !== '' && Object.keys(v).length) {
        localStorage.setItem('/dashboard/media-buying-posts', JSON.stringify(v));
      }
    },
  },

  computed: {
    cacheData() {
      return {
        ...this.cacheParams,
        typePosts: this.cacheParams.type,
        statusPosts: this.cacheParams.status,
      };
    },

    currency() {
      return this.currencyFormatter(
        this.$store.state.mediaBuying.campaign.payments_currency_code,
      );
    },
  },

  methods: {
    initDatepicker(params) {
      if (params?.created_at_from) {
        this.$refs.dataTable.setPeriod({
          from: params.created_at_from,
          to: params.created_at_to,
          period: params.period,
        });
      } else {
        this.$refs.dataTable.setPeriod(this.querySum.passedPeriod);
      }
    },

    fetchData: debounce(
      async function fetchData(params, silent) {
        if (!silent) this.loading = true;

        try {
          await fetch(this, params);
          this.cacheQuery();
        } finally {
          this.loading = false;
        }
      },
      1,
    ),

    async update(params, querySum, silent) {
      this.sortings = {
        sort_column: params.prop,
        sort_dir: params.order,
      };

      this.search = params.search;
      this.offset = params.offset;
      this.limit = params.limit;

      if (querySum) {
        this.querySum = querySum;
      }

      this.fetchData({
        sortings: this.sortings,
        ...params,
      }, silent);
    },

    getPayments(params) {
      this.$store.dispatch('getPayments', {
        ...params,
        campaign_id: this.$route.params.id,
      });
    },

    handleTabClick() {
      this.cacheQuery();
    },
    currencyFormatter(code, amount) {
      const miscCurrencies = this.$store.state.misc.currencies;
      const currency = miscCurrencies.find(
        (c) => (code ? code.toLowerCase() : null) === c.code?.toLowerCase(),
      );

      if (!Number.isFinite(amount)) {
        if (!code) {
          return '';
        }

        return currency ? currency.code.toUpperCase() : '';
      } else {
        amount = amount.toFixed(2);
        const amountData = amount.split('.');
        const result = (+amountData[0]).toLocaleString();

        if (!code) {
          return `${result}.${amountData[1]}`;
        }

        return currency
          ? `${currency.code.toUpperCase()} ${result}.${amountData[1]}`
          : `${result}.${amountData[1]}`;
      }
    },
    clearTooltip() {
      document.addEventListener(
        'mouseup',
        (this.listener = (e) => {
          const tooltip = document.querySelectorAll('.el-tooltip__popper');

          tooltip.forEach((e, i) => {
            tooltip[i].style.display = 'none';
          });
        }),
      );
    },
  },
  created() {
    this.$store.dispatch('getSubAffiliates');
    this.$store.dispatch('getPromocodeOffers');
    this.$store.dispatch('getTrafficSources', {});
    this.$store.dispatch('getCurrencies', {});
  },

  async activated() {
    this.loading = true;
    const q = JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'));

    this.initDatepicker(q);
    this.fetchData({
      activeTab: this.activeTab,
      sortings: this.sortings,
      querySum: this.querySum,
      ...q,
    });
    this.$refs.dataTable.initFilter(q);

    this.clearTooltip();
  },

  mounted() {
    const q = JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'));
    this.$refs.dataTable.initFilter(q);
    this.initDatepicker(q);

    this.$store.dispatch('uploadSettings');
    eventBus.$on('window-refresh', () => {
      const q = JSON.parse(localStorage.getItem('/dashboard/media-buying-posts'));
      this.$refs.dataTable.initFilter(q);
      this.initDatepicker(q);

      if (this.$route.name === 'routes.mediaBuyingPosts') {
        this.fetchData({
          activeTab: this.activeTab,
          sortings: this.sortings,
          querySum: this.querySum,
          ...q,
        });
      }
    });
    this.clearTooltip();
  },
  unmounted() {
    clearInterval(this.interval);
  },
  deactivated() {
    clearInterval(this.interval);
  },
};
</script>

<style lang="scss">
// vue3-migration -- '@/assets/theme/common-page-styles.scss'
// @import 'src/assets/theme/default/mediabuying/mediabuyingdetail';
</style>
